import React from 'react'
import { formatISO, isToday } from 'date-fns'
import { isEmpty } from 'lodash'
import {
  Symptom,
  SymptomLabels,
  QuestionnaireQuestionKeys as QuestionKeys,
  QuestionnaireAnswerEnum as AnswerEnum,
  getQuestionByKey,
  IQuestionnaire,
  getIndicatorColorByTestResult,
  CDC_QUESTIONNAIRE_CITATION,
  getFormattedDateAndTime,
} from '@modmd/data'
import { fontWeights, fontSizes, COLOR } from 'theme'
import Label from 'components/Label'
import { Text } from 'components/Typography'
import Button from 'components/Button'
import { DateInput } from 'components/DateInput'
import { Checkbox } from 'components/Checkbox'
import { Box } from 'components/Layout'
import TextInput from 'components/TextInput'
import { Answers } from './Answers'

interface UserDataLabelProps {
  label: string
  text?: string | null
  content?: React.ReactNode
}

const UserDataLabel = ({ label, text, content }: UserDataLabelProps) => (
  <Box my="0.75rem" display="flex" justifyContent="center" alignItems="center">
    <Text fontWeight={fontWeights.semibold}>{label}: </Text>
    <Box ml="0.25rem">{content ?? <Text>{text ?? '-'}</Text>}</Box>
  </Box>
)

export const QuestionnaireForm: React.VFC<IQuestionnaire> = ({
  questionnaireForm,
  isTemperatureShown = false,
  isMostRecentTestVisible = false,
  mostRecentTest,
  isDisabled = false,
  userData,
  onConfirm,
  isFetching,
  submittedAt,
}) => {
  const { questionnaireFormik, handleSetQuestionAnswer } = questionnaireForm
  const { values, errors } = questionnaireFormik

  const hasTestedPositive =
    values.surveyResponses.find(
      (response) => response.key === QuestionKeys.HAS_ALREADY_TESTED_POSITIVE_IN_PAST
    )?.value === AnswerEnum.YES

  const lastDatePositiveTest = values.surveyResponses.find(
    (response) => response.key === QuestionKeys.DATE_OF_FIRST_POSITIVE_RESULT
  )?.value

  const { result, testedAt } = mostRecentTest ?? {}

  if (submittedAt && !isToday(new Date(submittedAt))) {
    return (
      <Text color={COLOR.danger}>
        This person has not completed their daily questionnaire. Please have them log into the app
        to complete it.
      </Text>
    )
  }

  return (
    <>
      {userData && (
        <>
          <UserDataLabel label="Email" text={userData.email} />
          <UserDataLabel
            label="Name"
            text={`${userData.firstName ?? ''} ${userData.lastName ?? ''}`}
          />
          <UserDataLabel label="Phone number" text={userData.phoneNumber} />
        </>
      )}
      {isMostRecentTestVisible && (
        <>
          <UserDataLabel
            label="Last Test"
            text={testedAt ? getFormattedDateAndTime(testedAt, true) : 'Not tested yet'}
          />
          {result && (
            <UserDataLabel
              label="Result"
              content={
                <Label size="l" indicator={getIndicatorColorByTestResult(result)}>
                  {result}
                </Label>
              }
            />
          )}
        </>
      )}
      {!!submittedAt && (
        <Box my="1.5rem">
          <UserDataLabel
            label="Questionnaire Submission"
            text={getFormattedDateAndTime(submittedAt, true)}
          />
        </Box>
      )}
      <Answers
        questionnaireForm={questionnaireForm}
        questionKey={QuestionKeys.HAS_ALREADY_TESTED_POSITIVE_IN_PAST}
        isDisabled={isDisabled}
      />
      {hasTestedPositive && (
        <>
          <Text fontWeight={fontWeights.semibold} fontSize={fontSizes.l}>
            {getQuestionByKey(QuestionKeys.DATE_OF_FIRST_POSITIVE_RESULT)}
          </Text>
          <DateInput
            my="2rem"
            id="date-input"
            isFullWidth
            value={lastDatePositiveTest ? new Date(lastDatePositiveTest) : null}
            onChange={(date) =>
              handleSetQuestionAnswer({
                key: QuestionKeys.DATE_OF_FIRST_POSITIVE_RESULT,
                value: date ? formatISO(date) : '',
              })
            }
            disabled={isDisabled}
          />
        </>
      )}
      <Text fontWeight={fontWeights.semibold} fontSize={fontSizes.l}>
        {getQuestionByKey(QuestionKeys.SYMPTOMS)}
      </Text>
      <Box my="2rem">
        {Object.values(Symptom).map((symptom) => (
          <Checkbox
            key={symptom}
            label={SymptomLabels[symptom]}
            checked={values.surveyResponses.some(
              ({ key, value }) => key === symptom && value === AnswerEnum.YES
            )}
            onChange={() => {
              handleSetQuestionAnswer({
                key: symptom,
                value:
                  values.surveyResponses.find((response) => response.key === symptom)?.value ===
                  AnswerEnum.NO
                    ? AnswerEnum.YES
                    : AnswerEnum.NO,
              })
            }}
            disabled={isDisabled}
          />
        ))}
      </Box>
      <Answers
        questionnaireForm={questionnaireForm}
        questionKey={QuestionKeys.HAS_CLOSE_CONTANCT}
        isDisabled={isDisabled}
      />
      <Answers
        questionnaireForm={questionnaireForm}
        questionKey={QuestionKeys.HAS_TRAVELLED}
        isDisabled={isDisabled}
      />
      {isTemperatureShown && (
        <>
          <Text fontWeight={fontWeights.semibold} fontSize={fontSizes.l}>
            {getQuestionByKey(QuestionKeys.TEMPERATURE_IN_FAHRENHEIT)}
          </Text>
          <TextInput
            isFullWidth
            my="2rem"
            label="Enter temperature °F"
            value={
              values.surveyResponses.find(
                (response) => response.key === QuestionKeys.TEMPERATURE_IN_FAHRENHEIT
              )?.value ?? ''
            }
            onChange={(e) => {
              handleSetQuestionAnswer({
                key: QuestionKeys.TEMPERATURE_IN_FAHRENHEIT,
                value: e.target.value,
              })
            }}
            errorMessage={errors.temperatureError}
          />
        </>
      )}
      <Text textAlign="center">{CDC_QUESTIONNAIRE_CITATION}</Text>
      <Button appearance="link" href={process.env.REACT_APP_CDC_QUESTIONNAIRE} target="_blank">
        {process.env.REACT_APP_CDC_QUESTIONNAIRE}
      </Button>
      <Box my="2rem" display="flex" justifyContent="center">
        <Button
          width="100%"
          size="large"
          isFetching={isFetching}
          onClick={onConfirm}
          disabled={!isEmpty(errors)}
        >
          Send data
        </Button>
      </Box>
    </>
  )
}
